import { adminApi } from "@/common/lib/adminApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type AccessRequestStatus = "pending" | "approved" | "rejected" | "consumed" | "expired";

export type AdminAccessRequest = {
  id: string;
  email: string;
  phone: string;
  status: AccessRequestStatus;
  rejectReason: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  approvedLinkExpiresAt: string | null;
  consumedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AdminAccessRequestsList = {
  items: AdminAccessRequest[];
  total: number;
  page: number;
  pageSize: number;
};

export type AdminAccessRequestDetail = {
  request: AdminAccessRequest;
  emailAlreadyRegistered: boolean;
};

export function useAdminAccessRequestsList(params: {
  status?: AccessRequestStatus | "";
  page: number;
  pageSize: number;
}) {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  qs.set("page", String(params.page));
  qs.set("pageSize", String(params.pageSize));

  return useQuery({
    queryKey: ["admin", "access-requests", { status: params.status, page: params.page }],
    queryFn: () => adminApi.get<AdminAccessRequestsList>(`/access-requests?${qs}`),
    retry: 0,
  });
}

export function useAdminAccessRequestDetail(id: string) {
  return useQuery({
    queryKey: ["admin", "access-requests", id],
    queryFn: () => adminApi.get<AdminAccessRequestDetail>(`/access-requests/${id}`),
    retry: 0,
  });
}

export function useApproveAccessRequest() {
  const qc = useQueryClient();
  return useMutation<{ request: AdminAccessRequest }, Error, string>({
    mutationFn: (id) => adminApi.post(`/access-requests/${id}/approve`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "access-requests"] });
    },
  });
}

export function useRejectAccessRequest() {
  const qc = useQueryClient();
  return useMutation<{ request: AdminAccessRequest }, Error, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => adminApi.post(`/access-requests/${id}/reject`, { reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "access-requests"] });
    },
  });
}

export function useResendAccessRequest() {
  const qc = useQueryClient();
  return useMutation<{ request: AdminAccessRequest }, Error, string>({
    mutationFn: (id) => adminApi.post(`/access-requests/${id}/resend`),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ["admin", "access-requests"] });
      qc.removeQueries({ queryKey: ["admin", "access-requests", id] });
    },
  });
}
